/**
 * `/klienci/$clientId/plan/$assignmentId` — one plan, as a screen of its own: its Przegląd and its
 * calendar.
 *
 * Owner, 19 September 2026: the client's „Kalendarz" tab is the client's WHOLE calendar; the plan
 * calendar is a view of ONE plan and gets its own screen, framed like the programator. That reverses
 * the 13 August 2026 directive that put both calendars under one tab, and the "no redirect" note the
 * client route used to carry. Canon:
 * `docs/design/2026-09-19-kanon-kalendarz-planu-wlasny-ekran.html`, frames B–D and G0–G2.
 *
 * ── The TRAILING UNDERSCORE on `$clientId_` is load-bearing ───────────────────────────────────
 * TanStack Router's flat file routes nest by path segment, so `_shell.klienci.$clientId.plan.…`
 * would make `_shell.klienci.$clientId.tsx` this route's PARENT. That file is a leaf — it renders
 * the client detail and no `<Outlet/>` — so the child would never be drawn at all: measured, the
 * address resolved and the screen on it was the client detail's loading state.
 *
 * The trailing `_` is the router's own opt-out of that nesting (`$clientId_` is the same segment
 * for matching, and `/klienci/$clientId/plan/$assignmentId` is still the URL). It is what keeps the
 * client's identity bar and six tabs off this screen, which is the whole point of the move. **Do
 * not drop it, and do not add an `<Outlet/>` to the client route instead** — that would draw this
 * screen inside the frame the owner removed.
 *
 * Auth comes from `_shell.tsx`, once, like every other screen behind the gate.
 *
 * ── The search this screen carries, and why each key is here ──────────────────────────────────
 * · `view` — `calendar`, or absent for Przegląd, the default (owner, Q1, 27 September 2026): every
 *   link to a plan opens the overview. `overview` is never written; an address says `calendar` or
 *   nothing, so there is one spelling of each view. A link naming ONE SESSION (`exec`, with or
 *   without `comment`) opens the calendar with that session's panel whatever `view` says — the
 *   session lives on the calendar, and „Pokaż w planie" has always landed there.
 * · `exec` — a workout execution the panel opens on. The trainer's home queue, the client detail's
 *   activity rows and the client calendar's „Pokaż w planie" all write it.
 * · `comment` — put focus in that session's comment composer rather than on the panel's heading.
 *   DROPPED without `exec`, because it names nothing on its own.
 * · `q` / `f` / `s` — the roster list the client was opened from (`entities/client`'s
 *   `rosterContext`). They ride along so the way back restores the list rather than the unfiltered
 *   roster, and they are re-written explicitly on every navigate from here: a `search` updater
 *   REPLACES the object, so an omitted key is a dropped key.
 *
 * **Never `{...current, …}`.** TanStack's `applySearchMiddleware` spreads the validator's output
 * OVER the updater's result rather than replacing it, so an unknown key a link arrived with would
 * survive every navigation and be re-serialised forever. Every navigation below builds its search
 * from validated values, which is the plan builder's own pattern.
 */

import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router';

import {
  rosterContextOf,
  rosterContextSearch,
  type RosterContextSearch,
} from '~/entities/client';
import { AssignedPlanCalendarScreen, type PlanView } from '~/pages/assigned-plan-calendar';

interface PlanCalendarSearch extends RosterContextSearch {
  /** `calendar`; absent is Przegląd. */
  view?: 'calendar';
  /** A workout execution to open the session panel on. */
  exec?: number;
  /** Put focus in the session's comment composer rather than on the panel heading. */
  comment?: true;
}

/** A positive integer, or `undefined`. `?exec=` is a row id and nothing else. */
function positiveInt(raw: unknown): number | undefined {
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

export const Route = createFileRoute('/_shell/klienci/$clientId_/plan/$assignmentId')({
  validateSearch: (raw: Record<string, unknown>): PlanCalendarSearch => {
    const exec = positiveInt(raw.exec);
    // Normalised through the same reader the roster uses, then re-serialised with the defaults
    // dropped — so `?f=all` and `?s=nonsense` leave the address rather than staying as noise.
    const list = rosterContextSearch(rosterContextOf(raw));
    return {
      ...list,
      ...(raw.view === 'calendar' ? { view: 'calendar' as const } : {}),
      // Omitted rather than set to `undefined`: the router serialises a bare `?exec=` for the latter.
      ...(exec === undefined ? {} : { exec }),
      ...(exec !== undefined && (raw.comment === true || raw.comment === 'true')
        ? { comment: true as const }
        : {}),
    };
  },
  component: PlanCalendarRoute,
});

function PlanCalendarRoute() {
  const { clientId, assignmentId } = useParams({
    from: '/_shell/klienci/$clientId_/plan/$assignmentId',
  });
  const search = useSearch({ from: '/_shell/klienci/$clientId_/plan/$assignmentId' });
  const { exec, comment } = search;
  const navigate = useNavigate();

  // The list this client was opened from, re-written on every navigation from here.
  const list = rosterContextSearch(rosterContextOf(search));
  // A session named in the address lives on the calendar — see the header.
  const view: PlanView = exec !== undefined || search.view === 'calendar' ? 'calendar' : 'overview';
  /** The view's own key, written explicitly: `calendar`, or nothing for the default. */
  const viewSearch = (next: PlanView) => (next === 'calendar' ? { view: 'calendar' as const } : {});

  // A route parameter is a string and not necessarily a number at all. `null` short-circuits every
  // read rather than firing requests that can only 404.
  const client = Number(clientId);
  const clientIdOrNull = Number.isInteger(client) && client > 0 ? client : null;
  const assignment = Number(assignmentId);
  const assignmentIdOrNull = Number.isInteger(assignment) && assignment > 0 ? assignment : null;

  /** The client, with the list carried on — the identity link, the chevron and both empty states. */
  const toClient = {
    to: '/klienci/$clientId' as const,
    params: { clientId },
    search: list,
  };

  return (
    <AssignedPlanCalendarScreen
      clientId={clientIdOrNull}
      assignmentId={assignmentIdOrNull}
      openExecutionId={exec ?? null}
      focusComment={comment === true}
      clientLink={toClient}
      planLibraryLink={{ to: '/plans' }}
      view={view}
      onOpenOverview={() => {
        // The session panel's `?exec=` belongs to the calendar and goes with it.
        void navigate({
          to: '/klienci/$clientId/plan/$assignmentId',
          params: { clientId, assignmentId },
          search: list,
        });
      }}
      onOpenCalendar={() => {
        void navigate({
          to: '/klienci/$clientId/plan/$assignmentId',
          params: { clientId, assignmentId },
          search: { ...list, ...viewSearch('calendar') },
        });
      }}
      onBack={() => {
        // The client, not the roster: this screen was reached FROM a client and the chevron names
        // them. The list rides along so the detail's own two arrows still walk the filtered roster.
        void navigate(toClient);
      }}
      onPickPlan={(picked) => {
        if (picked === null) {
          // `Wszystkie wydarzenia` is not a way back — it is the way to the client's WHOLE
          // calendar, which is the Kalendarz tab. It PUSHES: leaving one plan for a different
          // surface is navigation the trainer took.
          void navigate({
            to: '/klienci/$clientId',
            params: { clientId },
            search: { ...list, tab: 'calendar' as const },
          });
          return;
        }
        // REPLACES rather than pushes: choosing another of this client's plans is looking around
        // one client, not navigating between them, and a history entry per glance would make Back
        // walk the trainer's own browsing instead of leaving the client.
        //
        // `?exec=` is dropped: a session belongs to one assignment, so carrying it onto another
        // plan's calendar would ask for an occurrence that plan does not contain. The VIEW is kept:
        // a trainer reading calendars reads the next plan's calendar.
        void navigate({
          to: '/klienci/$clientId/plan/$assignmentId',
          params: { clientId, assignmentId: String(picked) },
          search: { ...list, ...viewSearch(view) },
          replace: true,
        });
      }}
      onOpenBuilder={(planTemplateId, phase) => {
        void navigate({
          to: '/plans/$planId',
          params: { planId: String(planTemplateId) },
          // `?phase=` is the builder's own parameter and its validator owns the parsing; the key is
          // OMITTED rather than sent as `undefined`, which the router serialises as a bare `?phase=`.
          search: phase === null ? {} : { phase },
        });
      }}
    />
  );
}
